import notifier from '@albert-io/notifier';

import {resumeSession} from '@albert-io/mandark/authentication';
import {getQueryParamsAsObject} from 'client/history';

/**
 * Restores the user's session BEFORE the router mounts.
 *
 * Auth-gated routes (e.g. `/home`) guard synchronously in their `onEnter`
 * (`if (!sessionStore.hasValidSession()) replace('/log-in')`). On a cold page
 * load that guard would otherwise run before the asynchronous session restore
 * had populated the in-memory session, racing it and bouncing an authenticated
 * user to `/log-in`. Awaiting the restore here makes the session available on
 * the guards' first (and only) evaluation, fixing the whole class of auth-gated
 * routes at once.
 *
 * Skips the OAuth-error return flow (`?state=error`), mirroring the carve-out
 * that previously lived in the root route's `onEnter` (see `client/routes.js`).
 */
export async function restoreSessionBeforeBoot(): Promise<void> {
  if (getQueryParamsAsObject().state === 'error') {
    return;
  }
  // `resumeSession` resolves on every expected path (including the logged-out
  // 401 -> session/reset path). Catch the rare unexpected rejection (e.g. the
  // reset request itself failing) and report it instead of swallowing it, so a
  // network failure surfaces in telemetry but can never block app boot.
  try {
    await resumeSession();
  } catch (error) {
    notifier.notify(error, {
      message: 'Failed to restore session before boot',
      tags: 'authentication'
    });
  }
}
